import { useState } from "react";
import { CornerUpLeft, List, BarChart3, Images } from "lucide-react";
import { cn, formatWhatsAppMarkdown } from "@/lib/utils";
import type {
  WhatsAppCarouselDisplay,
  WhatsAppInteractiveDisplay,
  WhatsAppPollDisplay,
} from "@/lib/whatsapp-interactive-message";

type BubbleVariant = "outgoing" | "incoming" | "preview";

interface Props {
  interactive: WhatsAppInteractiveDisplay;
  variant?: BubbleVariant;
  className?: string;
}

export function WhatsAppInteractiveMessageBubble({
  interactive,
  variant = "incoming",
  className,
}: Props) {
  const isPreview = variant === "preview";
  const isOutgoing = variant === "outgoing";

  return (
    <div
      className={cn(
        "overflow-hidden rounded-lg text-sm",
        isPreview && "bg-[#1f2c34] text-[#e9edef] shadow-md",
        !isPreview && isOutgoing && "bg-transparent text-inherit",
        !isPreview && !isOutgoing && "bg-transparent text-inherit",
        className,
      )}
    >
      {interactive.bodyText && (
        <p
          className={cn("whitespace-pre-wrap px-3 pt-2.5 pb-1", isPreview && "text-[#e9edef]")}
          dangerouslySetInnerHTML={{ __html: formatWhatsAppMarkdown(interactive.bodyText) }}
        />
      )}

      {interactive.type === "button" && interactive.buttons && interactive.buttons.length > 0 && (
        <div className={cn("border-t", isPreview ? "border-[#2a3942]" : "border-current/10")}>
          {interactive.buttons.map((btn, idx) => (
            <div
              key={`${btn.id}-${idx}`}
              className={cn(
                "flex items-center justify-center gap-2 px-3 py-2.5 text-center font-medium",
                idx > 0 && cn("border-t", isPreview ? "border-[#2a3942]" : "border-current/10"),
                isPreview ? "text-[#53bdeb]" : isOutgoing ? "text-primary-foreground/95" : "text-primary",
              )}
            >
              <CornerUpLeft className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden />
              <span className="truncate">{btn.title}</span>
            </div>
          ))}
        </div>
      )}

      {interactive.type === "list" && interactive.menuButton && (
        <div className={cn("border-t", isPreview ? "border-[#2a3942]" : "border-current/10")}>
          <div
            className={cn(
              "flex items-center justify-center gap-2 px-3 py-2.5 text-center font-medium",
              isPreview ? "text-[#53bdeb]" : isOutgoing ? "text-primary-foreground/95" : "text-primary",
            )}
          >
            <List className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden />
            <span className="truncate">{interactive.menuButton}</span>
          </div>
        </div>
      )}
    </div>
  );
}

interface CarouselBubbleProps {
  carousel: WhatsAppCarouselDisplay;
  variant?: BubbleVariant;
  className?: string;
}

function CarouselCardImage({
  src,
  isPreview,
}: {
  src: string;
  isPreview: boolean;
}) {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <div
        className={cn(
          "flex aspect-[4/3] w-full items-center justify-center",
          isPreview ? "bg-[#2a3942]/40" : "bg-muted/30",
        )}
      >
        <Images
          className={cn("h-8 w-8", isPreview ? "text-[#8696a0]" : "opacity-40")}
          aria-hidden
        />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt=""
      className="aspect-[4/3] w-full object-cover"
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
    />
  );
}

export function WhatsAppCarouselMessageBubble({
  carousel,
  variant = "incoming",
  className,
}: CarouselBubbleProps) {
  const isPreview = variant === "preview";
  const isOutgoing = variant === "outgoing";

  return (
    <div
      className={cn(
        "overflow-hidden rounded-lg text-sm",
        isPreview && "bg-[#1f2c34] text-[#e9edef] shadow-md",
        !isPreview && isOutgoing && "bg-transparent text-inherit",
        !isPreview && !isOutgoing && "bg-transparent text-inherit",
        className,
      )}
    >
      {carousel.bodyText && (
        <p
          className={cn("whitespace-pre-wrap px-3 pt-2.5 pb-2", isPreview && "text-[#e9edef]")}
          dangerouslySetInnerHTML={{ __html: formatWhatsAppMarkdown(carousel.bodyText) }}
        />
      )}

      <div
        className={cn(
          "flex gap-2 overflow-x-auto px-3 pb-2.5 snap-x snap-mandatory",
          isPreview ? "scrollbar-thin" : "",
        )}
      >
        {carousel.cards.map((card, idx) => (
          <div
            key={`carousel-card-${idx}`}
            className={cn(
              "min-w-[180px] max-w-[200px] shrink-0 snap-start overflow-hidden rounded-lg border",
              isPreview ? "border-[#2a3942] bg-[#0b141a]" : "border-current/15 bg-background/5",
            )}
          >
            {card.imageUrl ? (
              <CarouselCardImage src={card.imageUrl} isPreview={isPreview} />
            ) : (
              <div
                className={cn(
                  "flex aspect-[4/3] w-full items-center justify-center",
                  isPreview ? "bg-[#2a3942]/40" : "bg-muted/30",
                )}
              >
                <Images
                  className={cn("h-8 w-8", isPreview ? "text-[#8696a0]" : "opacity-40")}
                  aria-hidden
                />
              </div>
            )}

            <div className="px-2.5 py-2">
              <p
                className={cn(
                  "line-clamp-3 text-xs whitespace-pre-wrap",
                  isPreview ? "text-[#e9edef]" : "",
                )}
                dangerouslySetInnerHTML={{ __html: formatWhatsAppMarkdown(card.text) }}
              />
            </div>

            {(card.buttons ?? []).length > 0 && (
              <div className={cn("border-t", isPreview ? "border-[#2a3942]" : "border-current/10")}>
                {(card.buttons ?? []).map((btn, btnIdx) => (
                  <div
                    key={`${btn.id}-${btnIdx}`}
                    className={cn(
                      "flex items-center justify-center gap-1.5 px-2 py-2 text-center text-xs font-medium",
                      btnIdx > 0 && cn("border-t", isPreview ? "border-[#2a3942]" : "border-current/10"),
                      isPreview ? "text-[#53bdeb]" : isOutgoing ? "text-primary-foreground/95" : "text-primary",
                    )}
                  >
                    <CornerUpLeft className="h-3 w-3 shrink-0 opacity-80" aria-hidden />
                    <span className="truncate">{btn.text}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {isPreview && (
        <p className="px-3 pb-2 text-[10px] text-[#8696a0]">
          Prévia — no WhatsApp o contato desliza entre os cartões.
        </p>
      )}
    </div>
  );
}

interface PollBubbleProps {
  poll: WhatsAppPollDisplay;
  variant?: BubbleVariant;
  className?: string;
}

export function WhatsAppPollMessageBubble({
  poll,
  variant = "incoming",
  className,
}: PollBubbleProps) {
  const isPreview = variant === "preview";
  const subtitle = poll.allowMultiple ? "Selecione uma ou mais" : "Selecione uma opção";

  return (
    <div
      className={cn(
        "overflow-hidden rounded-lg text-sm",
        isPreview && "bg-[#005c4b] text-[#e9edef] shadow-md",
        !isPreview && "bg-transparent text-inherit",
        className,
      )}
    >
      <div className={cn("px-3 pt-2.5 pb-1", isPreview && "text-[#e9edef]")}>
        <div className="flex items-start gap-2">
          <BarChart3
            className={cn("mt-0.5 h-4 w-4 shrink-0", isPreview ? "text-[#8696a0]" : "opacity-60")}
            aria-hidden
          />
          <div className="min-w-0 flex-1">
            <p
              className="font-medium whitespace-pre-wrap"
              dangerouslySetInnerHTML={{ __html: formatWhatsAppMarkdown(poll.question) }}
            />
            <p className={cn("mt-0.5 text-xs", isPreview ? "text-[#8696a0]" : "opacity-70")}>
              {subtitle}
            </p>
          </div>
        </div>
      </div>

      <div className={cn("border-t", isPreview ? "border-[#2a3942]/60" : "border-current/10")}>
        {poll.options.map((opt, idx) => (
          <div
            key={`${opt.id}-${idx}`}
            className={cn(
              "flex items-center gap-3 px-3 py-2.5",
              idx > 0 && cn("border-t", isPreview ? "border-[#2a3942]/60" : "border-current/10"),
            )}
          >
            <span
              className={cn(
                "h-4 w-4 shrink-0 rounded-full border-2",
                isPreview ? "border-[#8696a0]" : "border-current/40",
              )}
              aria-hidden
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <span className="truncate">{opt.title}</span>
                {isPreview && (
                  <span className="shrink-0 text-xs text-[#8696a0]">0</span>
                )}
              </div>
              {isPreview && (
                <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-[#2a3942]/80">
                  <div className="h-full w-0 rounded-full bg-[#00a884]" />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {isPreview && (
        <p className="px-3 py-2 text-[10px] text-[#8696a0]">
          Prévia — no WhatsApp o contato toca em &quot;Votar&quot; para escolher uma opção.
        </p>
      )}
    </div>
  );
}

interface InboundReplyProps {
  title: string;
  description?: string;
  kind: "button" | "list";
  isPollVote?: boolean;
  isOutgoing?: boolean;
  className?: string;
}

export function WhatsAppInteractiveReplyBubble({
  title,
  description,
  kind,
  isPollVote = false,
  isOutgoing = false,
  className,
}: InboundReplyProps) {
  return (
    <div className={cn("space-y-1", className)}>
      <div
        className={cn(
          "inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium",
          isOutgoing
            ? "bg-primary-foreground/15 text-primary-foreground/90"
            : "bg-primary/10 text-primary",
        )}
      >
        {isPollVote ? (
          <BarChart3 className="h-3 w-3 shrink-0" aria-hidden />
        ) : kind === "list" ? (
          <List className="h-3 w-3 shrink-0" aria-hidden />
        ) : (
          <CornerUpLeft className="h-3 w-3 shrink-0" aria-hidden />
        )}
        <span>
          {isPollVote ? "Voto na enquete" : kind === "list" ? "Opção selecionada" : "Resposta rápida"}
        </span>
      </div>
      <p className="font-medium whitespace-pre-wrap">{title}</p>
      {description && (
        <p className={cn("text-xs whitespace-pre-wrap", isOutgoing ? "opacity-80" : "text-muted-foreground")}>
          {description}
        </p>
      )}
    </div>
  );
}

